import DateFnsUtils from '@date-io/date-fns'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Theme, Typography, makeStyles } from '@material-ui/core'
import {
    KeyboardDatePicker,
    MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { MONTH_DATE_YEAR_FORMAT } from 'bpm/pages/constants'
import { format, isValid } from 'date-fns'
import React, { FC, useCallback, useMemo } from 'react'
import { LABEL_MISSING_USER_NAME } from '../ListingNotes.constants'
import { NoteTypeReasonOption } from '../ListingNotes.types'
import colors from '../PDNColors'
import FilterAltOff from '../assets/FilterAltOff'
import usePDNFiltersHook from '../hooks/usePDNFiltersHook'
import usePDNhook from '../hooks/usePDNHook'
import NoteTypeFilter from './NoteTypeFilter'
import UserFilterInput from './UserFilterInput'

const useStyles = makeStyles((theme: Theme) => ({
    elementCard: {
        top: theme.spacing(-1) /* required */,
        position: 'sticky',
        flex: '0 0 100%',
        width: '80vw',
        maxWidth: pxToRem(1200),
        margin: theme.spacing(-1, 'auto', 1),
        backgroundColor: 'inherit',
    },
    filterContainer: {
        width: '100%',
        display: 'flex',
        position: 'sticky',
        justifyContent: 'space-between',
        alignContent: 'center',
    },
    dateInputContainer: {
        flex: 5,
        marginRight: theme.spacing(1),
    },
    dateInput: {
        marginTop: 0,
        '& .MuiInputBase-root': {
            backgroundColor: theme.palette.common.white,
            marginTop: theme.spacing(1),
            padding: theme.spacing(0, 0, 0, 1),
            height: pxToRem(47),
        },
    },
    typeSelect: {
        backgroundColor: theme.palette.common.white,
        margin: theme.spacing(1, 1, 0, 1),
        paddingLeft: theme.spacing(1),
        width: '100%',
        height: pxToRem(47),
    },
    clearAllIcon: {
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: colors.customerNoteColor,
        opacity: ({ hasFilters }: { hasFilters?: boolean }) =>
            hasFilters ? '100%' : '40%',
        '&:hover': {
            cursor: ({ hasFilters }: { hasFilters?: boolean }) =>
                hasFilters ? 'pointer' : 'default',
        },
    },
}))

const NotesFilters: FC<{ noteTypes: NoteTypeReasonOption[] }> = ({
    noteTypes,
}) => {
    const { PDNUserList, totalCount } = usePDNhook()
    const { filters, setNotesFilters, clearFilters } = usePDNFiltersHook()
    const hasFilters = useMemo(
        () =>
            !!filters.users.length ||
            !!filters.noteTypes.length ||
            !!filters.dateFrom ||
            !!filters.dateTo,
        [filters]
    )
    const classes = useStyles({ hasFilters })

    const users = useMemo(() => {
        const uniqueUsers: string[] = []
        
        PDNUserList.map((user: any) => {
            const name = user.profileInfo 
                ? `${user.profileInfo.firstName} ${user.profileInfo.lastName}` 
                : LABEL_MISSING_USER_NAME
            if(uniqueUsers.indexOf(name) === -1)
                uniqueUsers.push(name)
        })
        return uniqueUsers
    }, [PDNUserList])

    const setSelectedUsers = useCallback(
        (userValues) => {
            setNotesFilters({ ...filters, users: userValues })
        },
        [filters, setNotesFilters]
    )

    const setSelectedNoteTypes = useCallback(
        (noteTypeValues) => {
            setNotesFilters({ ...filters, noteTypes: noteTypeValues })
        },
        [filters, setNotesFilters]
    )

    const handleDateChange = useCallback(
        (type) => (date: Date | null) => {
            if (!isValid(date) && date !== null) {
                return
            }

            if (type === 'from') {
                setNotesFilters({
                    ...filters,
                    dateFrom: !!date
                        ? format(date, MONTH_DATE_YEAR_FORMAT)
                        : null,
                })
            } else {
                setNotesFilters({
                    ...filters,
                    dateTo: !!date
                        ? format(date, MONTH_DATE_YEAR_FORMAT)
                        : null,
                })
            }
        },
        [filters, setNotesFilters, format]
    )

    const handleResetFilters = useCallback(() => {
        if (!!hasFilters) {
            clearFilters()
        }
    }, [clearFilters, hasFilters])

    return (
        <GridItem className={classes.elementCard}>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Typography component="div" className={classes.filterContainer}>
                    <UserFilterInput
                        disabled={!totalCount}
                        setUsers={setSelectedUsers}
                        selectedUsers={filters.users}
                        users={users as any}
                        data-testid="usernameFilterInput"
                    ></UserFilterInput>

                    <NoteTypeFilter
                        disabled={!totalCount}
                        noteTypes={noteTypes}
                        selectedNoteTypes={filters.noteTypes}
                        setNoteTypes={setSelectedNoteTypes}
                    />

                    <Typography
                        data-testid="dateFrom"
                        className={classes.dateInputContainer}
                    >
                        <KeyboardDatePicker
                            disabled={!totalCount}
                            data-testid="notesDateFromPicker"
                            disableToolbar
                            autoOk
                            variant="inline"
                            maxDate={
                                !!filters.dateTo
                                    ? new Date(filters.dateTo)
                                    : new Date()
                            }
                            format={MONTH_DATE_YEAR_FORMAT}
                            margin="normal"
                            className={classes.dateInput}
                            id="date-picker-inline-from"
                            placeholder="Date from"
                            value={filters.dateFrom}
                            onChange={handleDateChange('from')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </Typography>
                    <Typography
                        data-testid="dateTo"
                        className={classes.dateInputContainer}
                    >
                        <KeyboardDatePicker
                            disableToolbar
                            data-testid="notesDateToPicker"
                            disabled={!totalCount}
                            autoOk
                            variant="inline"
                            maxDate={new Date()}
                            minDate={new Date(filters.dateFrom ?? '')}
                            format={MONTH_DATE_YEAR_FORMAT}
                            margin="normal"
                            className={classes.dateInput}
                            id="date-picker-inline-to"
                            placeholder="Date to"
                            value={filters.dateTo}
                            onChange={handleDateChange('to')}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </Typography>
                    <HoverTooltip text={hasFilters ? 'Clear All Filters' : ''}>
                        <Typography
                            className={classes.clearAllIcon}
                            component="div"
                            data-testid="clearAllFilters"
                            onClick={handleResetFilters}
                        >
                            <FilterAltOff />
                        </Typography>
                    </HoverTooltip>
                </Typography>
            </MuiPickersUtilsProvider>
        </GridItem>
    )
}

export default NotesFilters
